// Admin v2 catalog — /admin/packages
//
// New UI per spec: Package | Price | Credits/mo | Sites | Setup fee |
// Stripe sync | Active | Actions. Sync All button calls sync-stripe-catalog.
// Edit modal toggles features per package (the existing legacy editor at
// /admin/packages/legacy stays for back-compat with v1 catalog management).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, RefreshCw, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { db, money } from "@/lib/billing";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

type CatalogRow = {
  id: string;
  slug: string;
  name: string;
  price_cents: number | null;
  currency: string;
  is_active: boolean;
  legacy: boolean;
  sort_order: number;
  max_websites: number | null;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  package_features: { feature_key: string; feature_value: unknown }[];
  setup_fees: { amount_cents: number; currency: string }[];
};

async function fetchCatalog(): Promise<CatalogRow[]> {
  const { data, error } = await db
    .from("packages")
    .select("id, slug, name, price_cents, currency, is_active, legacy, sort_order, max_websites, stripe_price_id, stripe_product_id, package_features (feature_key, feature_value), setup_fees (amount_cents, currency)")
    .eq("legacy", false)
    .order("sort_order");
  if (error) throw error;
  return (data as CatalogRow[]) ?? [];
}

function getCredits(row: CatalogRow): number {
  const pf = row.package_features.find((p) => p.feature_key === "monthly_credits");
  const fv = pf?.feature_value as { amount?: number } | undefined;
  return Number(fv?.amount ?? 0);
}

export default function AdminCatalogPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(toLocale(i18n.language));
  const qc = useQueryClient();
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState<CatalogRow | null>(null);

  const catalog = useQuery({ queryKey: ["admin-catalog-v2"], queryFn: fetchCatalog });

  async function syncAll() {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-stripe-catalog", { body: {} });
      if (error) throw error;
      // deno-lint-ignore no-explicit-any
      const summary = data as any;
      toast({ description: `Synced ${summary?.synced ?? "ok"}` });
      qc.invalidateQueries({ queryKey: ["admin-catalog-v2"] });
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : "Sync failed" });
    } finally {
      setSyncing(false);
    }
  }

  async function toggleActive(row: CatalogRow) {
    try {
      const { error } = await db.from("packages").update({ is_active: !row.is_active }).eq("id", row.id);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["admin-catalog-v2"] });
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : String(e) });
    }
  }

  const rows = catalog.data ?? [];

  return (
    <div className="container mx-auto py-6 space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("admin.catalogTitle", { defaultValue: "Catalog" })}</h1>
          <p className="text-sm text-muted-foreground">
            <Link to="/admin/packages/legacy" className="underline">
              {t("admin.legacyEditor", { defaultValue: "Manage legacy v1 packages" })}
            </Link>
          </p>
        </div>
        <Button onClick={syncAll} disabled={syncing}>
          {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          {t("admin.syncAll", { defaultValue: "Sync All" })}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {catalog.isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading")}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Package</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Credits / mo</TableHead>
                  <TableHead>Sites</TableHead>
                  <TableHead>Setup fee</TableHead>
                  <TableHead>Stripe sync</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const credits = getCredits(row);
                  const setup = row.setup_fees?.[0]?.amount_cents ?? null;
                  const synced = !!row.stripe_price_id;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="font-medium">{row.name}</div>
                        <div className="text-xs text-muted-foreground">{row.slug}</div>
                      </TableCell>
                      <TableCell>
                        {row.price_cents === null ? <Badge variant="outline">Custom</Badge> : money(row.price_cents / 100, row.currency, false, locale)}
                      </TableCell>
                      <TableCell>{credits.toLocaleString()}</TableCell>
                      <TableCell>{row.max_websites ?? "∞"}</TableCell>
                      <TableCell>
                        {setup === null ? "—" : money(setup / 100, row.currency, false, locale)}
                      </TableCell>
                      <TableCell>
                        {synced ? (
                          <Badge variant="secondary" className="gap-1"><Check className="h-3 w-3" />Synced</Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 text-muted-foreground"><X className="h-3 w-3" />Missing</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch checked={row.is_active} onCheckedChange={() => toggleActive(row)} />
                      </TableCell>
                      <TableCell className="text-right">
                        <IconButton variant="ghost" className="h-8 w-8" onClick={() => setEditing(row)} label={t("actions.edit")}>
                          <Pencil className="h-4 w-4" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PackageEditDialog package={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["admin-catalog-v2"] })} />
    </div>
  );
}

function PackageEditDialog({ package: pkg, onClose, onSaved }: {
  package: CatalogRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  // Working copy of feature_value rows keyed by feature_key.
  const initial = useMemo(() => {
    const map: Record<string, unknown> = {};
    for (const pf of pkg?.package_features ?? []) map[pf.feature_key] = pf.feature_value;
    return map;
  }, [pkg]);
  const [features, setFeatures] = useState<Record<string, unknown>>(initial);

  useMemo(() => setFeatures(initial), [initial]);

  if (!pkg) return null;

  async function save() {
    if (!pkg) return;
    setBusy(true);
    try {
      // Persist each toggled boolean feature.
      // deno-lint-ignore no-explicit-any
      const dba = db as any;
      const updates = Object.entries(features).map(async ([key, value]) => {
        const { data: existing } = await dba
          .from("package_features")
          .select("id")
          .eq("package_id", pkg!.id)
          .eq("feature_key", key)
          .maybeSingle();
        if (existing) {
          await dba.from("package_features").update({ feature_value: value }).eq("id", existing.id);
        }
      });
      await Promise.all(updates);
      toast({ description: "Saved." });
      onSaved();
      onClose();
    } catch (e) {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const toggleable = ["editorial_qa", "white_label"] as const;

  return (
    <Dialog open={!!pkg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{pkg.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {toggleable.map((key) => (
            <div key={key} className="flex items-center justify-between border rounded-md p-3">
              <div>
                <Label className="font-medium">{key}</Label>
                <p className="text-xs text-muted-foreground">{String(features[key] ?? "—")}</p>
              </div>
              <Switch
                checked={features[key] === true}
                onCheckedChange={(v) => setFeatures((prev) => ({ ...prev, [key]: v }))}
              />
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Other feature values (credits, max_sources, ai_images …) edit via the legacy editor for now.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
